import * as R from 'ramda';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	ON_DRAG,
	ON_DRAG_START,
	ON_DRAG_END,
	ON_LENGTH_CHANGE,
	ON_RESIZE,
	SET_ONBOARDING_WIDTH,
	SAVE_PREDRAG_WIDTHS,
	SET_ACTIVE_SEPARATOR,
	SET_IS_HOVERED,
	type Action,
} from './actions';
import type {
	OnDragPayload,
	OnResizePayload,
	SectionData,
	SectionsData,
	SectionsState,
	State,
} from './types';
import {
	getAvailableDelta,
	getExpansionDelta,
	getSectionsWidth,
	scaleLeftSections,
	scaleRightSections,
	getDeltaFromRightFlexiSection,
} from './util';

export const initialState: State = {
	sections: {},
	isDragging: false,
} as const;

export const mapSectionsDataToStateOld = (sectionsData: SectionsData): SectionsState =>
	sectionsData.reduce(
		(sectionsState: SectionsState, section: SectionData): SectionsState => {
			// eslint-disable-next-line no-param-reassign
			sectionsState.widths[section.id] = section.width;
			// eslint-disable-next-line no-param-reassign
			sectionsState.minWidths[section.id] = section.minWidth;
			return sectionsState;
		},
		{ widths: {}, minWidths: {} },
	);

export const mapSectionsDataToState = (sectionsData: SectionsData): SectionsState => {
	const minWidths: Record<string, number> = {};
	const widths: Record<string, number> = {};
	sectionsData.forEach((section) => {
		minWidths[section.id] = section.minWidth;
		widths[section.id] = section.width;
	});

	return { widths, minWidths };
};

const reducer = (state: State = initialState, action: Action): State => {
	switch (action.type) {
		case SET_ACTIVE_SEPARATOR: {
			const { id, index } = action;
			return R.assocPath(['sections', id, 'activeSeparatorIndex'], index, state);
		}
		case SET_IS_HOVERED: {
			const { id, index } = action;
			return R.assocPath(['sections', id, 'hoveredSectionIndex'], index, state);
		}

		case SET_ONBOARDING_WIDTH: {
			const { id: mainSectionId, sectionsData, width } = action;

			const newWidths: Record<string, number> = {};
			sectionsData.forEach(({ onboardingWidth, id }) => {
				if (onboardingWidth) {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					newWidths[id] = (width! * onboardingWidth) / 100;
				} else {
					newWidths[id] = state.sections[mainSectionId].widths[id];
				}
			});

			return R.assocPath(['sections', mainSectionId, 'widths'], newWidths, state);
		}

		case SAVE_PREDRAG_WIDTHS: {
			const { id } = action;
			const sections = state.sections[id];
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const preDragWidths: Record<string, any> = {};
			for (const sectionKey of Object.keys(sections.widths)) {
				preDragWidths[sectionKey] =
					sections.minWidths[sectionKey] !== sections.widths[sectionKey]
						? sections.widths[sectionKey]
						: sections.preDragWidths && sections.preDragWidths[sectionKey];
			}
			return R.assocPath(['sections', id, 'preDragWidths'], preDragWidths, state);
		}
		case ON_DRAG_START: {
			return { ...state, isDragging: true };
		}
		case ON_DRAG_END: {
			return { ...state, isDragging: false };
		}
		case ON_DRAG: {
			const { id, index, delta, sectionsData, parent, totalWidth, isFitParent } = action.payload;
			const sections = state.sections[id];

			// "No delta" means that it's not an actual dragging but section expansion, and delta should be calculated.
			const sizeDelta = R.isNil(delta)
				? getExpansionDelta(sectionsData[index], sections)
				: delta || 0;

			const direction = Math.sign(sizeDelta);
			const availableDelta = getAvailableDelta(
				{ id, index, parent, sectionsData, totalWidth },
				direction,
				isFitParent,
			);

			// If there is no space for expansion, let's try to take some from the previous section
			// by emulating its dragging in opposite direction.
			if (
				availableDelta === 0 &&
				index > 0 &&
				sectionsData[index].width <= sectionsData[index].minWidth
			) {
				const payload: OnDragPayload = {
					...action.payload,
					index: index - 1,
					// "minus abs" ensures that delta stays negative when cascading deeper than just previous section.
					delta: -Math.abs(sizeDelta),
				};
				return reducer(state, {
					type: ON_DRAG,
					payload,
				});
			}

			// Restrict delta magnitude by available space but keep the direction.
			const deltaToApply = direction * Math.min(Math.abs(sizeDelta), Math.abs(availableDelta));

			// Just a micro-optimization.
			if (deltaToApply === 0) return state;

			const previousSectionsWidth = getSectionsWidth(sectionsData);

			const sectionsOnTheLeft = sectionsData.slice(0, index + 1);
			const sectionsOnTheRight = sectionsData.slice(index + 1);

			const newSectionsData = [
				...scaleLeftSections(deltaToApply, sectionsOnTheLeft),
				// If there is no parent then ask scaleRightSections to keep total width unchanged
				// if possible. Which means to try to compensate change on the left side even if
				// there are no flex sections on the right.
				...scaleRightSections(deltaToApply, sectionsOnTheRight, !parent),
			];

			const nextSectionsWidth = getSectionsWidth(newSectionsData);
			const totalWidthDelta = nextSectionsWidth - previousSectionsWidth;

			const isDragOpposite =
				!isFitParent &&
				((direction < 0 && totalWidthDelta > 0) || (direction > 0 && totalWidthDelta < 0));

			// Don't update widths from dragging opposite direction
			if (isDragOpposite) return state;

			let newState = state;

			// Total width of sections has been changed which means we "borrowed" some available
			// delta from the parent. Now it's time to ask the parent to drag itself a bit.
			if (totalWidthDelta !== 0 && parent) {
				const payload: OnDragPayload = { ...parent, delta: totalWidthDelta, isFitParent };
				newState = reducer(state, {
					type: ON_DRAG,
					payload,
				});
			}

			const newSectionsState = ff('portfolio-3-no-param-reassign-fix_topgz')
				? mapSectionsDataToState(newSectionsData)
				: mapSectionsDataToStateOld(newSectionsData);

			return R.assocPath(['sections', id], { ...sections, ...newSectionsState }, newState);
		}
		case ON_RESIZE: {
			const {
				id,
				width: sectionWidth,
				sectionsData,
				minWidth,
				isFitParent,
				parent,
			} = action.payload;

			const sections = state.sections[id];

			if (sectionsData.length === 0) return state;

			const sectionsWidth = getSectionsWidth(sectionsData);

			let width = !isFitParent ? Math.max(minWidth ?? sectionsWidth, sectionsWidth) : sectionWidth;

			let delta;
			let newSectionsState = {};

			if (
				isFitParent ||
				minWidth === undefined ||
				/**
				 * Avoid changing the columns width if the sectionsWidth is greater than the minWidth to respect the persisted widths,
				 * allow changing the columns width when the sectionsWidth is smaller than minWidth to fill up the remaining space.
				 */
				sectionsWidth <= minWidth
			) {
				// @ts-expect-error - TS7053 - width can be undefined
				delta = width - sectionsWidth;
			} else if (
				!isFitParent &&
				minWidth &&
				width &&
				/**
				 * Only applicable when the new width is greater than the viewport.
				 */
				width > minWidth
			) {
				/**
				 * Apply special treatment to the last non-fixed column:
				 * Scale the other columns and take delta from the last non-fixed column if it's width is greater than minWidth.
				 *
				 */
				const deltaFromFlexiCol = getDeltaFromRightFlexiSection(sectionsData);
				delta = -Math.min(width - minWidth, deltaFromFlexiCol);
				width += delta;
			} else if (width === sections?.totalWidth) {
				/**
				 * Short-circuit if the width hasn't changed
				 */
				return state;
			}

			if (delta !== undefined) {
				// Resize entire Sections component by resizing the rightmost section.
				const index = sectionsData.length - 1;

				const sectionsOnTheLeft = sectionsData.slice(0, index + 1);
				const sectionsOnTheRight = sectionsData.slice(index + 1);

				const newSectionsData = [
					...scaleLeftSections(delta, sectionsOnTheLeft),
					...scaleRightSections(delta, sectionsOnTheRight, false),
				];

				newSectionsState = ff('portfolio-3-no-param-reassign-fix_topgz')
					? mapSectionsDataToState(newSectionsData)
					: mapSectionsDataToStateOld(newSectionsData);
			}

			let newState = state;
			/**
			 * if the section has isFitParent=false, it doesn't bound by its parent's width,
			 * therefore, we have to update the parent's width when the section resized.
			 */
			if (parent && sectionWidth && width && sectionWidth !== width && !isFitParent) {
				// Get the difference between the width from sectionsData AND the width from state
				const diffWidth = width - sectionWidth;

				const { totalWidth, sectionsData: parentSectionsData, ...restParent } = parent;

				const parentIndex = parentSectionsData.length - 1;

				const parentSectionsOnTheLeft = parentSectionsData.slice(0, parentIndex + 1);
				const parentSectionsOnTheRight = parentSectionsData.slice(parentIndex + 1);

				const newSectionsData = [
					...scaleLeftSections(diffWidth, parentSectionsOnTheLeft),
					...scaleRightSections(diffWidth, parentSectionsOnTheRight, true),
				];

				const payload: OnResizePayload = {
					...restParent,
					isFitParent: false,
					width: totalWidth,
					sectionsData: newSectionsData,
				};
				newState = reducer(state, {
					type: ON_RESIZE,
					payload,
				});
			}

			return R.assocPath(
				['sections', id],
				{ ...sections, totalWidth: width, ...newSectionsState },
				newState,
			);
		}
		case ON_LENGTH_CHANGE: {
			const { id, width, sectionsData, parent, isFitParent } = action.payload;
			const sections = state.sections[id];

			/**
			 * If there is no parent OR if the section is not fitting the parent,
			 * then it's not "borrowing" any width from parent.
			 *
			 * In that case adding or removing section is the same as if sections were without/with
			 * added/removed section with their current individual widths and then were resized to
			 * the former total width.
			 */
			if (!parent || !isFitParent) {
				const payload: OnResizePayload = action.payload;
				return reducer(state, { type: ON_RESIZE, payload });
			}

			// Otherwise we can use space available in the parent to minimize impact of
			// adding/removal section on other sections widths.

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const delta = getSectionsWidth(sectionsData) - width!;
			const direction = Math.sign(delta);

			const availableDelta = getAvailableDelta(parent, direction, isFitParent);
			// Restrict delta magnitude by available space but keep the direction.
			const deltaToApply = direction * Math.min(Math.abs(delta), Math.abs(availableDelta));

			// Now let's ask the parent to drag itself a bit to accomodate adding/removing section.
			const payload: OnDragPayload = {
				...parent,
				delta: deltaToApply,
				isFitParent,
			};
			const newState = reducer(state, {
				type: ON_DRAG,
				payload,
			});

			// Next step is to scale sections to compensate delta not absorbed by the parent.

			const availableWidth = sectionsData.reduce(
				(acc, section) => acc + section.width - section.minWidth,
				0,
			);

			if (availableWidth <= 0) {
				return newState;
			}

			// Proportionally distribute remaining delta over all sections disregarding their isFixed status.
			const excessiveDelta = delta - deltaToApply;
			const k = excessiveDelta / availableWidth;
			const newSectionsData = sectionsData.map((section) => ({
				...section,
				width: Math.max(section.minWidth, section.width - k * (section.width - section.minWidth)),
			}));

			const newSectionsState = ff('portfolio-3-no-param-reassign-fix_topgz')
				? mapSectionsDataToState(newSectionsData)
				: mapSectionsDataToStateOld(newSectionsData);
			return R.assocPath(
				['sections', id],
				{ ...sections, totalWidth: width, ...newSectionsState },
				newState,
			);
		}
		default: {
			const _exhaustiveCheck: never = action;
			return state;
		}
	}
};

export default reducer;
